 
import {useEffect, useState, useRef, FC} from "react";
import {
    Stack,
    TextField,
    TooltipHost,
    DirectionalHint,
    MessageBar,
    MessageBarType
} from "@fluentui/react";
import {Button} from "@fluentui/react-components";
import {
    Send28Filled,
    Add24Regular,
    Document24Regular,
    Dismiss16Regular,
    RecordStopRegular
} from "@fluentui/react-icons";

import styles from "./QuestionInput.module.css";
import DropdownTreeSelector from "@/features/application/components/DropdownTreeSelector";
import {IBusinessUnit} from "@/features/application/api/applicationModels";

enum ValidationErrorType {
    QUESTION_REQUIRED = "QUESTION_REQUIRED",
    LOCATION_REQUIRED = "LOCATION_REQUIRED"
}

const ValidationMessages = {
    [ValidationErrorType.QUESTION_REQUIRED]: "Please enter a question",
    [ValidationErrorType.LOCATION_REQUIRED]: "Please select a restaurant"
};

interface IProps {
    onSend: (question: string, fileContent?: string, fileName?: string) => void;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    clientTreeHierarchy: IBusinessUnit[];
    selectedBusinessUnitId: string;
    handleBusinessUnitChange: (businessUnitId: string) => void;
    isRequestActive?: boolean;
    onCancel: () => void;
    disabled?: boolean;
}

// The base component that receives all props
export const QuestionInput: FC<IProps> = ({
    onSend,
    placeholder,
    clearOnSend,
    initQuestion,
    clientTreeHierarchy,
    selectedBusinessUnitId,
    handleBusinessUnitChange,
    isRequestActive,
    onCancel,
    disabled
}) => {
    const [question, setQuestion] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileContent, setFileContent] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [validationErrorType, setValidationErrorType] =
        useState<ValidationErrorType | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropdownContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    const sendQuestion = () => {
        if (isRequestActive) {
            return;
        }

        if (!question.trim()) {
            setValidationErrorType(ValidationErrorType.QUESTION_REQUIRED);
            setValidationError(
                ValidationMessages[ValidationErrorType.QUESTION_REQUIRED]
            );
            return;
        }

        if (!selectedBusinessUnitId) {
            setValidationErrorType(ValidationErrorType.LOCATION_REQUIRED);
            setValidationError(
                ValidationMessages[ValidationErrorType.LOCATION_REQUIRED]
            );
            return;
        }

        setValidationError(null);
        setValidationErrorType(null);

        // Send the question along with file content and name if available
        onSend(question, fileContent || undefined, selectedFile?.name);

        if (clearOnSend) {
            setQuestion("");
            handleRemoveFile();
        }
    };

    // Clear validation errors when conditions are satisfied
    useEffect(() => {
        if (!validationErrorType) return;

        // Clear question required error when question is valid
        if (
            validationErrorType === ValidationErrorType.QUESTION_REQUIRED &&
            question.trim() !== ""
        ) {
            setValidationError(null);
            setValidationErrorType(null);
        }

        // Clear location required error when business unit is valid
        if (
            validationErrorType === ValidationErrorType.LOCATION_REQUIRED &&
            selectedBusinessUnitId !== ""
        ) {
            setValidationError(null);
            setValidationErrorType(null);
        }
    }, [question, selectedBusinessUnitId, validationErrorType]);

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (
        _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    const readFileContent = (file: File) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const content = e.target?.result as string;
            setFileContent(content);
        };

        reader.onerror = () => {
            console.error("Error reading file");
            setFileContent(null);
        };

        reader.readAsText(file);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            readFileContent(file);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileContent(null);
        // Reset the file input so the same file can be re-selected if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <Stack className={styles.questionInputContainer}>
            <Stack.Item>
                <TextField
                    className={styles.questionInputTextArea}
                    placeholder={placeholder}
                    multiline
                    resizable={false}
                    borderless
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    disabled={disabled}
                />
            </Stack.Item>

            {selectedFile && (
                <Stack.Item>
                    <div className={styles.selectedFileContainer}>
                        <Document24Regular className={styles.fileIcon} />
                        <span className={styles.fileName}>{selectedFile.name}</span>
                        <button
                            className={styles.removeFileButton}
                            onClick={handleRemoveFile}
                            aria-label="Remove file"
                        >
                            <Dismiss16Regular />
                        </button>
                    </div>
                </Stack.Item>
            )}

            <Stack.Item>
                <div
                    className={`${styles.buttonContainer}${
                        disabled ? " disabled-opacity" : ""
                    }`}
                >
                    <div className={styles.leftButtons}>
                        <TooltipHost
                            content="Upload Your Own Data"
                            directionalHint={DirectionalHint.bottomCenter}
                        >
                            <Button
                                size="medium"
                                icon={<Add24Regular />}
                                onClick={handleFileButtonClick}
                                className={styles.uploadButton}
                                disabled={disabled}
                            />
                        </TooltipHost>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{display: "none"}}
                            accept=".csv,.txt"
                        />
                    </div>

                    <div ref={dropdownContainerRef}>
                        <DropdownTreeSelector
                            clientTreeHierarchy={clientTreeHierarchy}
                            selectedValue={selectedBusinessUnitId}
                            onSelect={(selectedBusinessUnitId) => {
                                handleBusinessUnitChange(selectedBusinessUnitId);
                            }}
                            getPopupContainer={() =>
                                dropdownContainerRef.current || document.body
                            }
                            dropdownPopupAlign={{
                                points: ["bl", "tl"],
                                offset: [0, -4]
                            }}
                            disabled={disabled}
                        />
                    </div>

                    <div className={styles.rightButtons}>
                        <TooltipHost
                            content={isRequestActive ? "Cancel" : "Send"}
                            directionalHint={DirectionalHint.bottomCenter}
                        >
                            {isRequestActive ? (
                                <Button
                                    size="large"
                                    aria-label="Cancel"
                                    icon={
                                        <RecordStopRegular primaryFill="rgba(115, 118, 225, 1)" />
                                    }
                                    onClick={onCancel}
                                />
                            ) : (
                                <Button
                                    size="large"
                                    aria-label="Send"
                                    icon={
                                        <Send28Filled primaryFill="rgba(115, 118, 225, 1)" />
                                    }
                                    onClick={sendQuestion}
                                    disabled={disabled}
                                />
                            )}
                        </TooltipHost>
                    </div>
                </div>

                {validationError && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        dismissButtonAriaLabel="Close"
                        className={styles.validationMessageBar}
                        styles={{
                            root: {
                                marginTop: "8px",
                                borderRadius: "8px"
                            }
                        }}
                    >
                        {validationError}
                    </MessageBar>
                )}
            </Stack.Item>
        </Stack>
    );
};
